// src/modules/communications/components/CommunicationHistory.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FaPhone,
  FaPhoneSlash,
  FaSms,
  FaLongArrowAltLeft,
  FaLongArrowAltRight,
  FaPlayCircle,
  FaHistory,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaSpinner,
  FaReply,
  FaVolumeUp,
  FaImage
} from 'react-icons/fa';
import { getLeadCommunicationHistory } from '../services/communicationService';
import { 
  formatDateTime, 
  formatDuration, 
  getStatusColor, 
  formatPhoneNumber 
} from '../utils/formatUtils';
import { useCommunication } from '../context/CommunicationContext';
import Pagination from '../../../components/shared/Pagination';
import TranscriptionSection from '../../../components/communications/TranscriptionSection';

const PaginationContainer = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
  display: flex;
  justify-content: center;
`;

const HistoryContainer = styled.div`
  width: 100%;
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Title = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Tab = styled.button`
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  .count {
    font-size: ${props => props.theme.typography.fontSize.small};
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.inputGray}33;
    padding: 0 ${props => props.theme.spacing(1)};
    border-radius: 10px;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  padding-left: ${props => props.theme.spacing(3)};
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 15px;
    width: 2px;
    background-color: ${props => props.theme.colors.inputGray}33;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: ${props => props.theme.spacing(4)};
  padding-left: ${props => props.theme.spacing(4)};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineIcon = styled.div`
  position: absolute;
  left: -12px;
  width: 32px;
  height: 32px;
  background-color: ${props => props.type === 'call' 
    ? props.direction === 'inbound' ? props.theme.colors.success : props.theme.colors.primary 
    : props.direction === 'inbound' ? props.theme.colors.success : props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 1;
`;

const TimelineContent = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  .timeline-type {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
  }
  
  .timeline-time {
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const TimelineDirection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const TimelineStatus = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 10px;
  font-size: ${props => props.theme.typography.fontSize.small};
  background-color: ${props => props.color}22;
  color: ${props => props.color};
  margin-left: ${props => props.theme.spacing(1)};
`;

const CallDetails = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  
  .call-duration {
    margin-bottom: ${props => props.theme.spacing(1)};
  }
  
  .call-recording {
    margin-top: ${props => props.theme.spacing(2)};
    
    audio {
      width: 100%;
    }
  }
  
  .call-notes {
    margin-top: ${props => props.theme.spacing(2)};
    padding: ${props => props.theme.spacing(1.5)};
    background-color: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius};
    font-style: italic;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const MessageBody = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  padding: ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  white-space: pre-wrap;
`;

const MediaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1)};
`;

const AudioPlayerInline = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1.5)};
  border-left: 3px solid ${props => props.theme.colors.primary};
  margin-top: ${props => props.theme.spacing(1)};
  width: 100%;

  .audio-icon {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  audio {
    flex: 1;
    min-width: 0;
    height: 36px;
  }
`;

const MediaThumb = styled.a`
  display: block;
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray}33;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const isAudioUrl = (url) => {
  const ext = url.split('.').pop().split('?')[0].toLowerCase();
  return ['amr', 'ogg', 'mp3', 'wav', 'm4a', '3gp', 'aac', 'mp4'].includes(ext);
};

const TimelineActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
  text-align: center;
  
  svg {
    font-size: 3rem;
    color: ${props => props.theme.colors.inputGray};
    margin-bottom: ${props => props.theme.spacing(2)};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${props => props.theme.spacing(4)};
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * Communication History component for displaying a lead's communication history
 * @param {Object} props - Component props
 * @param {string} props.leadId - ID of the lead
 * @returns {React.ReactElement} Communication History component
 */
const CommunicationHistory = ({ leadId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'call', 'sms'

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Show 5 items per page
  const [totalItems, setTotalItems] = useState(0);
  
  const { openCallModal, openSmsComposer } = useCommunication();

  const URL_API = process.env.REACT_APP_API_PUBLIC || "https://api.mrpatchasphalt.com";
  
  // Fetch communication history when leadId changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!leadId) return;
      
      setLoading(true);
      try {
        const data = await getLeadCommunicationHistory(leadId);
        setHistory(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching communication history:', err);
        setError('Failed to load communication history');
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [leadId]);
    
  const getFilteredHistory = () => {
    // First filter by type
    const filtered = activeTab === 'all' 
      ? history 
      : history.filter(item => item.type === activeTab);
    
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    
    // Return paginated results
    return filtered.slice(indexOfFirstItem, indexOfLastItem);
  };
  
  // Get paginated data
  const filteredHistory = getFilteredHistory();
  
  // Calculate total pages
  const totalPages = Math.ceil(
    (activeTab === 'all' 
      ? history.length 
      : history.filter(item => item.type === activeTab).length) / itemsPerPage
  );
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of the component
    window.scrollTo({ top: window.pageYOffset - 200, behavior: 'smooth' });
  };
  
  // Count calls and SMS
  const callCount = history.filter(item => item.type === 'call').length;
  const smsCount = history.filter(item => item.type === 'sms').length;
  
  // Handle call button click
  const handleCall = (phoneNumber) => {
    openCallModal(phoneNumber, leadId);
  };
  
  // Handle SMS button click
  const handleSms = (phoneNumber) => {
    openSmsComposer(phoneNumber, leadId);
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <FaSpinner />
      </LoadingContainer>
    );
  }
  
  if (error) {
    return (
      <EmptyState>
        <FaHistory />
        <p>{error}</p>
      </EmptyState>
    );
  }
  
  if (history.length === 0) {
    return (
      <EmptyState>
        <FaHistory />
        <p>No communication history found for this lead.</p>
      </EmptyState>
    );
  }
  
  return (
    <HistoryContainer>
      <HistoryHeader>
        <Title>
          <FaHistory />
          Communication History
        </Title>
      </HistoryHeader>
      
      <Tabs>
        <Tab 
          active={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All <span className="count">{history.length}</span>
        </Tab>
        <Tab 
          active={activeTab === 'call'} 
          onClick={() => setActiveTab('call')}
        >
          <FaPhone /> Calls <span className="count">{callCount}</span>
        </Tab>
        <Tab 
          active={activeTab === 'sms'} 
          onClick={() => setActiveTab('sms')}
        >
          <FaSms /> Messages <span className="count">{smsCount}</span>
        </Tab>
      </Tabs>
      
      {filteredHistory.length === 0 ? (
        <EmptyState>
          <FaFilter />
          <p>No {activeTab === 'call' ? 'calls' : activeTab === 'sms' ? 'messages' : 'communications'} found.</p>
        </EmptyState>
      ) : (

        <>
        <TimelineContainer>
          {filteredHistory.map((item, index) => (
            <TimelineItem key={`${item.type}-${item.id}`}>
              <TimelineIcon type={item.type} direction={item.direction}>
                {item.type === 'call' ? (
                  item.direction === 'inbound' ? <FaPhone /> : <FaPhoneSlash />
                ) : (
                  <FaSms />
                )}
              </TimelineIcon>
              
              <TimelineContent>
                <TimelineHeader>
                  <span className="timeline-type">
                    {item.type === 'call' ? 'Call' : 'SMS'}
                    <TimelineStatus color={getStatusColor(item.status)}>
                      {item.status}
                    </TimelineStatus>
                  </span>
                  <span className="timeline-time">{formatDateTime(item.timestamp)}</span>
                </TimelineHeader>
                
                <TimelineDirection>
                  {item.direction === 'inbound' ? (
                    <>
                      <FaLongArrowAltLeft /> 
                      From {formatPhoneNumber(item.from)} to {formatPhoneNumber(item.to)}
                    </>
                  ) : (
                    <>
                      <FaLongArrowAltRight />
                      From {formatPhoneNumber(item.from)} to {formatPhoneNumber(item.to)}
                    </>
                  )}
                </TimelineDirection>
                
                {item.type === 'call' ? (
                  <CallDetails>
                    <div className="call-duration">
                      Duration: {formatDuration(item.duration)}
                    </div>
                    
                    {item.recordingUrl && (
                      <div className="call-recording">
                        <audio controls src={`${URL_API}${item.recordingUrl}`}>
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                    
                    {item.notes && (
                      <div className="call-notes">
                        Notes: {item.notes}
                      </div>
                    )}
                    {item.recordingUrl && (
                      <TranscriptionSection
                        callId={item.id}
                        transcription={item.transcription}
                        transcriptionStatus={item.transcription_status}
                        transcribedAt={item.transcribed_at}
                      />
                    )}
                  </CallDetails>
                ) : (
                  <>
                  <MessageBody>
                    {item.body}
                  </MessageBody>
                  {(item.media_paths || item.mediaPaths || []).length > 0 && (
                    <MediaContainer>
                      {(item.media_paths || item.mediaPaths || []).map((url, idx) =>
                        isAudioUrl(url) ? (
                          <AudioPlayerInline key={idx}>
                            <FaVolumeUp className="audio-icon" />
                            <audio controls preload="metadata">
                              <source src={`${URL_API}${url}`} />
                            </audio>
                          </AudioPlayerInline>
                        ) : (
                          <MediaThumb key={idx} href={`${URL_API}${url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${URL_API}${url}`} alt={`Media ${idx + 1}`} />
                          </MediaThumb>
                        )
                      )}
                    </MediaContainer>
                  )}
                  </>
                )}
                
                <TimelineActions>
                  {item.type === 'call' ? (
                    <ActionButton onClick={() => handleCall(item.direction === 'inbound' ? item.from : item.to)}>
                      <FaPhone /> Call Back
                    </ActionButton>
                  ) : (
                    <ActionButton onClick={() => handleSms(item.direction === 'inbound' ? item.from : item.to)}>
                      <FaReply /> Reply
                    </ActionButton>
                  )}
                </TimelineActions>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>

        {/* Add pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </PaginationContainer>
          )}
        </>        
      )}
    </HistoryContainer>
  );
};

export default CommunicationHistory;